import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CompatibilityTestResult {
  score: number;
  aiSummary: string;
  commonPoints: string[];
  differences: string[];
  rawResponse: any;
  fromCache: boolean;
}

export interface ProfileOption {
  id: string;
  username: string;
  role: string | null;
  gender: string | null;
  main_photo_url: string | null;
}

export function useAdminCompatibilityTest() {
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState<CompatibilityTestResult | null>(null);

  const fetchProfiles = useCallback(async () => {
    setIsLoadingProfiles(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, role, gender, main_photo_url')
        .eq('onboarding_completed', true)
        .order('username');

      if (error) throw error;

      setProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('Erro ao carregar perfis');
    } finally {
      setIsLoadingProfiles(false);
    }
  }, []);

  const deleteCacheForPair = useCallback(async (user1Id: string, user2Id: string) => {
    try {
      const { data, error } = await supabase
        .from('profile_compatibility')
        .delete()
        .or(`and(user_id.eq.${user1Id},target_user_id.eq.${user2Id}),and(user_id.eq.${user2Id},target_user_id.eq.${user1Id})`)
        .select();
      
      if (error) throw error;
      
      console.log('[AdminCompatibilityTest] Deleted cache for pair:', user1Id, user2Id, 'rows:', data?.length || 0);
      return data?.length || 0;
    } catch (error) {
      console.error('[AdminCompatibilityTest] Error deleting cache:', error);
      throw error;
    }
  }, []);

  const calculateCompatibility = useCallback(async (
    currentUserId: string,
    targetUserId: string,
    options: { skipCache?: boolean } = {}
  ) => {
    setIsCalculating(true);
    setResult(null);

    try {
      // If skipCache, delete existing cache first
      if (options.skipCache) {
        await deleteCacheForPair(currentUserId, targetUserId);
      }

      // Check if cache exists (when not skipping)
      if (!options.skipCache) {
        const { data: cached } = await supabase
          .from('profile_compatibility')
          .select('*')
          .eq('user_id', currentUserId)
          .eq('target_user_id', targetUserId)
          .maybeSingle();

        if (cached) {
          setResult({
            score: cached.compatibility_score,
            aiSummary: cached.ai_summary || '',
            commonPoints: cached.common_points || [],
            differences: cached.differences || [],
            rawResponse: cached,
            fromCache: true,
          });
          toast.info('Resultado carregado do cache');
          setIsCalculating(false);
          return;
        }
      }

      // Call Edge Function with isAdminTest flag to bypass gem check
      const { data, error } = await supabase.functions.invoke('calculate-compatibility', {
        body: { currentUserId, targetUserId, isAdminTest: true }
      });

      if (error) throw error;

      setResult({
        score: data.compatibility_score,
        aiSummary: data.ai_summary || '',
        commonPoints: data.common_points || [],
        differences: data.differences || [],
        rawResponse: data,
        fromCache: false,
      });

      toast.success('Compatibilidade calculada!');
    } catch (error) {
      console.error('Error calculating compatibility:', error);
      toast.error('Erro ao calcular compatibilidade');
    } finally {
      setIsCalculating(false);
    }
  }, [deleteCacheForPair]);

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  return {
    profiles,
    isLoadingProfiles,
    isCalculating,
    result,
    fetchProfiles,
    calculateCompatibility,
    clearResult,
  };
}
